import { useState } from "react";

// What an OAuth integration needs when the provider API doesn't declare its
// credential keys — the plain client-credentials pair.
export const DEFAULT_OAUTH_CREDENTIAL_KEYS = ["client_id", "client_secret"];

// Stringify a numeric field for an input value, but show "" (not "null") for a
// null/undefined value, and preserve a real 0 as "0".
const numStr = (v, dflt) => (v === null || v === undefined ? dflt : String(v));

export default function useConfigurationForm(
  service,
  existingIntegration = null
) {
  // Helper function to parse custom headers (JSON string or object) to textarea string
  const parseCustomHeaders = (headers) => {
    if (!headers) {
      return "";
    }

    // If headers is a string, try to parse it as JSON
    let headersObj = headers;
    if (typeof headers === "string") {
      try {
        headersObj = JSON.parse(headers);
      } catch (e) {
        // If parsing fails, treat as no custom headers
        return "";
      }
    }

    // Convert object to textarea format
    if (!headersObj || Object.keys(headersObj).length === 0) {
      return "";
    }

    return Object.entries(headersObj)
      .map(([key, value]) => `${key}: ${value}`)
      .join("\n");
  };

  // Map auth_type from API to connectionMethod
  const getConnectionMethod = (authType) => {
    const typeMap = {
      api_key: "apikey",
      mcp: "mcp",
      oauth: "oauth",
      webhook: "webhook",
      no_auth: "noauth"
    };
    return typeMap[authType] || "apikey";
  };

  // Common state
  const [activeTab, setActiveTab] = useState("authentication");
  const [connectionMethod, setConnectionMethod] = useState(
    existingIntegration
      ? getConnectionMethod(existingIntegration.auth_type)
      : "apikey"
  );

  // Secrets are encrypted at rest and never returned to the client, so we never
  // prefill them on edit — a blank secret field means "keep the existing one"
  // (ConfigurationModal omits secret_ref when blank). Non-secret fields (base_url)
  // are hydrated from the existing integration.
  // API Key state
  const [apiKey, setApiKey] = useState("");
  const [environment, setEnvironment] = useState("production");
  const [region, setRegion] = useState("us-east");
  const [apiEndpoint, setApiEndpoint] = useState(
    existingIntegration?.auth_type === "api_key"
      ? existingIntegration?.base_url || ""
      : ""
  );

  // MCP state
  const [mcpServerUrl, setMcpServerUrl] = useState(
    existingIntegration?.auth_type === "mcp"
      ? existingIntegration?.base_url || ""
      : ""
  );
  const [protocolVersion, setProtocolVersion] = useState("v1.0");
  const [timeout, setTimeout] = useState("30");
  const [authToken, setAuthToken] = useState("");
  const [permissions, setPermissions] = useState({
    readAccess: true,
    writeAccess: false,
    executeWorkflows: false,
    adminPrivileges: false
  });

  // OAuth state.
  //
  // The credential keys are whatever this provider API's auth handler actually
  // reads (service.credential_fields), not a hardcoded client_id/client_secret
  // pair — Experian's password grant needs four. Falls back to the conventional
  // pair when the API does not declare a list.
  const oauthCredentialKeys =
    Array.isArray(service?.credential_fields) && service.credential_fields.length
      ? service.credential_fields
      : DEFAULT_OAUTH_CREDENTIAL_KEYS;
  const [oauthCredentials, setOauthCredentials] = useState({});
  const setOauthCredential = (key, value) =>
    setOauthCredentials((prev) => ({ ...prev, [key]: value }));

  // Webhook state
  // NOTE: this host is a placeholder — orbit.nativeorange.com does not resolve.
  // Webhook delivery is configured through the webhook registry, not here.
  const webhookUrl = `https://orbit.nativeorange.com/webhooks/${service.name
    .toLowerCase()
    .replace(/\s+/g, "-")}`;
  const [eventTypes, setEventTypes] = useState({
    dataUpdates: true,
    statusChanges: true,
    errorNotifications: false,
    systemAlerts: false
  });
  const [retryPolicy, setRetryPolicy] = useState("exponential");

  // Settings state - populate from existingIntegration if available
  const [requestTimeout, setRequestTimeout] = useState(
    existingIntegration
      ? numStr(existingIntegration.request_timeout_ms, "30000")
      : "30000"
  );
  const [rateLimit, setRateLimit] = useState(
    existingIntegration ? numStr(existingIntegration.rate_limit_rpm, "") : "60"
  );
  const [maxRetries, setMaxRetries] = useState(
    existingIntegration ? numStr(existingIntegration.max_retries, "3") : "3"
  );
  const [cacheTtl, setCacheTtl] = useState(
    existingIntegration ? numStr(existingIntegration.cache_ttl_seconds, "") : "300"
  );
  const [enableCaching, setEnableCaching] = useState(
    existingIntegration?.enable_data_caching ?? true
  );
  const [logRequests, setLogRequests] = useState(
    existingIntegration?.log_api_requests ?? true
  );
  const [enableCompression, setEnableCompression] = useState(
    existingIntegration?.enable_compression ?? true
  );
  const [sslVerification, setSslVerification] = useState(
    existingIntegration?.ssl_verify ?? true
  );
  const [customHeaders, setCustomHeaders] = useState(
    existingIntegration
      ? parseCustomHeaders(existingIntegration.custom_headers)
      : ""
  );

  // Monitoring state - populate from existingIntegration if available
  const [errorThreshold, setErrorThreshold] = useState(
    existingIntegration ? numStr(existingIntegration.error_threshold, "") : "5"
  );
  const [latencyThreshold, setLatencyThreshold] = useState(
    existingIntegration
      ? numStr(existingIntegration.latency_threshold_ms, "")
      : "2000"
  );
  const [notificationChannel, setNotificationChannel] = useState(
    existingIntegration?.notification_channel || "email"
  );
  const [alertTypes, setAlertTypes] = useState({
    connectionFailures: existingIntegration?.notify_connection_failures ?? true,
    rateLimitWarnings: existingIntegration?.notify_rate_limit_warnings ?? true,
    highLatencyAlerts: existingIntegration?.notify_high_latency_alerts ?? true,
    authenticationErrors:
      existingIntegration?.notify_authentication_errors ?? true,
    dataValidationErrors:
      existingIntegration?.notify_data_validation_errors ?? false,
    weeklyUsageSummary: existingIntegration?.weekly_usage_summary ?? false
  });

  return {
    // Common
    activeTab,
    setActiveTab,
    connectionMethod,
    setConnectionMethod,
    // API Key
    apiKey,
    setApiKey,
    environment,
    setEnvironment,
    region,
    setRegion,
    apiEndpoint,
    setApiEndpoint,
    // MCP
    mcpServerUrl,
    setMcpServerUrl,
    protocolVersion,
    setProtocolVersion,
    timeout,
    setTimeout,
    authToken,
    setAuthToken,
    permissions,
    setPermissions,
    // OAuth
    oauthCredentialKeys,
    oauthCredentials,
    setOauthCredential,
    // Webhook
    webhookUrl,
    eventTypes,
    setEventTypes,
    retryPolicy,
    setRetryPolicy,
    // Settings
    requestTimeout,
    setRequestTimeout,
    rateLimit,
    setRateLimit,
    maxRetries,
    setMaxRetries,
    cacheTtl,
    setCacheTtl,
    enableCaching,
    setEnableCaching,
    logRequests,
    setLogRequests,
    enableCompression,
    setEnableCompression,
    sslVerification,
    setSslVerification,
    customHeaders,
    setCustomHeaders,
    // Monitoring
    errorThreshold,
    setErrorThreshold,
    latencyThreshold,
    setLatencyThreshold,
    notificationChannel,
    setNotificationChannel,
    alertTypes,
    setAlertTypes
  };
}
